import { PouchSubType, PouchDetailsItems, DenominationLadderDetails } from '@api/generator';

import { CURRENCY_ITEM_IDS } from '@constants';
import {
  denominationsForDenominationLadderType,
  RawDenomination,
  rawDenominations,
} from '@constants/denominations';

import { DenominationInfo, DenominationType } from './DenominationLadderTypes';

// ***** Validation Functions *****

export const pouchSubTypeDenominationTypeMap: Record<PouchSubType, DenominationType> = {
  useable_notes: 'note',
  non_useable_notes: 'unusableNote',
  non_useable_coins: 'unusableCoin',
  useable_coins_full_bag: 'fullBagCoins',
  useable_coins_part_bag: 'partBagCoins',
};

const denominationItemTypeDenominationTypeMap: Record<
  RawDenomination['itemType'],
  DenominationType
> = {
  coin: 'coin',
  fullBag: 'fullBagCoins',
  note: 'note',
  partBag: 'partBagCoins',
  unusableCoin: 'unusableCoin',
  unusableNote: 'unusableNote',
} as Record<RawDenomination['itemType'], DenominationType>;

export const transformPouchDetailsToDenominationInfo = (pouchDetails: PouchDetailsItems) => {
  if (!pouchDetails) return [];
  return Object.values(pouchDetails).map((pouch) => {
    const { itemID, itemQuantity, itemValue } = pouch;
    const rawDenomination = itemID ? rawDenominations[itemID] : undefined;

    const itemType =
      rawDenomination && denominationItemTypeDenominationTypeMap[rawDenomination.itemType];
    return {
      itemID,
      quantity: itemQuantity || 0,
      value: itemValue ? `${itemValue}` : '0',
      type: itemType,
    };
  });
};

export const formatDenominationLabel = (ladderItem: RawDenomination): string => {
  let amount = '';
  // specifically for the unusable notes and coins that are not specified
  if (ladderItem.denomination === null) {
    amount = 'Non-Usable';
  } else if (ladderItem.currency === 'GBP') {
    amount = `£${ladderItem.denomination}.00`;
  } else {
    amount = `${ladderItem.denomination}`;
  }
  const p = ladderItem.currency === 'p' ? 'p' : '';
  const materialType = ladderItem.materialType === 'COIN' ? 'Coins' : 'Notes';

  return `${amount}${p} ${materialType}`;
};

export const denominationValuePence = (rawDenomination: RawDenomination): number | null => {
  if (rawDenomination.denomination === null || rawDenomination.currency === null) return null;
  const multiplier = rawDenomination.currency === 'GBP' ? 100 : 1;
  const quantity = rawDenomination.maxQuantityInBag ?? 1;
  return rawDenomination.denomination * multiplier * quantity;
};

const getCompleteDenominationsList = () => Object.values(CURRENCY_ITEM_IDS).map((item) => [item]);

export const getDenominationBasedOnPouchSubType = (
  pouchSubType: PouchSubType,
  pouchDenominations?: void | DenominationLadderDetails | undefined,
) => {
  if (pouchDenominations?.pouchSubType[pouchSubType]) {
    return pouchDenominations?.pouchSubType[pouchSubType];
  }
  return denominationsForDenominationLadderType[pouchSubType] ?? getCompleteDenominationsList();
};

export const denominationLadderForPouchSubType = (
  pouchSubType: PouchSubType,
  pouchDenominations?: void | DenominationLadderDetails,
): DenominationInfo[] | null => {
  const denominations = getDenominationBasedOnPouchSubType(pouchSubType, pouchDenominations);

  if (!denominations) {
    return [];
  }
  const denominationInfoType = pouchSubTypeDenominationTypeMap?.[pouchSubType];

  return denominations.map((itemIDs) => {
    if (pouchSubType === 'useable_coins_part_bag') {
      // For part bag pouches, itemIDs will have 2 items. First item is the part bag, second item is the individual note/coin
      const partBagDenomination = pouchDenominations
        ? pouchDenominations?.rawDenominations[itemIDs[0]]
        : rawDenominations[itemIDs[0]];
      const looseCoinsDenomination = pouchDenominations
        ? pouchDenominations?.rawDenominations[itemIDs[1]]
        : rawDenominations[itemIDs[1]];

      const label = formatDenominationLabel(looseCoinsDenomination);
      const unitValuePence = denominationValuePence(looseCoinsDenomination);
      const partBagCoins: DenominationInfo['partBagCoins'] = {
        itemID: partBagDenomination?.itemID,
        maxQuantityInBag: partBagDenomination?.maxQuantityInBag,
        type: 'partBagCoins',
      };
      const coin: DenominationInfo['coin'] = {
        itemID: looseCoinsDenomination?.itemID,
        maxQuantityInBag: null,
        type: 'coin',
      };
      const denominationAmount = partBagDenomination?.denomination;
      const currency = partBagDenomination?.currency;

      return {
        itemID: looseCoinsDenomination?.itemID,
        label,
        unitValuePence,
        type: denominationInfoType,
        maxQuantityInBag: null,
        partBagCoins,
        coin,
        denominationAmount,
        currency,
      };
    }

    // For non-part-bags, itemIDs will only have 1 item. The loose coins/notes
    const denomination = pouchDenominations
      ? pouchDenominations?.rawDenominations[itemIDs[0]]
      : rawDenominations[itemIDs[0]];
    const itemID = itemIDs[0];
    const label = formatDenominationLabel(denomination);
    const unitValuePence = denominationValuePence(denomination);
    const type = denominationInfoType;
    const maxQuantityInBag = denomination?.maxQuantityInBag;
    const denominationAmount = denomination?.denomination;
    const currency = denomination?.currency;

    return {
      itemID,
      label,
      unitValuePence,
      type,
      maxQuantityInBag,
      denominationAmount,
      currency,
    };
  });
};

export const isDenominationPartBag = (denomination: DenominationInfo) =>
  !!denomination.partBagCoins;

export const getMaxQuantityInBag = (denomination: DenominationInfo) => {
  return isDenominationPartBag(denomination)
    ? denomination?.partBagCoins?.maxQuantityInBag
    : denomination?.maxQuantityInBag;
};

export const getDenominationID = (
  { partBagCoins, coin, itemID }: DenominationInfo,
  type: 'quantity' | 'partBagCoins' | 'looseCoins' | 'value',
): string | undefined => {
  if (partBagCoins && coin) {
    if (type === 'quantity') {
      return partBagCoins.itemID;
    } else if (type === 'looseCoins') {
      return coin.itemID;
    } else {
      return partBagCoins.itemID;
    }
  }
  return itemID;
};

export const getDenominationFieldName = (
  denomination: DenominationInfo,
  type: 'quantity' | 'looseCoins' | 'value',
) => {
  const itemID = getDenominationID(denomination, type);

  if (type === 'quantity') return `quantity-${itemID}`;
  if (type === 'value') return `value-${itemID}`;
  if (type === 'looseCoins') return `looseCoins-${itemID}`;
  return '';
};

export const getBagQuantityValue = (quantity: number, denomination: DenominationInfo) => {
  const unitValuePence = denomination.unitValuePence ?? 0;
  let total = quantity * (unitValuePence || 0);
  if (isDenominationPartBag(denomination)) {
    const maxQuantityInBag = getMaxQuantityInBag(denomination);
    total = total * (maxQuantityInBag ?? 0);
  }
  return total;
};

export const getBagLooseCoinsValue = (
  looseCoinQuantity: number,
  denomination: DenominationInfo,
) => {
  const unitValuePence = denomination.unitValuePence ?? 0;
  return looseCoinQuantity * unitValuePence;
};

export const updateDenominationWithValues = (
  existingDenomination: DenominationInfo,
  fieldValues: Record<string, number>,
  values: { value?: number; quantity?: number; looseCoins?: number; fieldValue?: string },
) => {
  const quantity =
    values?.quantity ?? fieldValues?.[getDenominationFieldName(existingDenomination, 'quantity')];
  const coinCount =
    values?.looseCoins ??
    fieldValues?.[getDenominationFieldName(existingDenomination, 'looseCoins')];
  const value =
    values?.value ?? fieldValues?.[getDenominationFieldName(existingDenomination, 'value')];

  const isPartBag = isDenominationPartBag(existingDenomination);

  if (!isPartBag) {
    existingDenomination.value = `${value}`;
    existingDenomination.quantity = values.fieldValue === '' ? undefined : quantity;
  } else {
    const bagValue = getBagQuantityValue(quantity, existingDenomination);
    const looseCoinsValue = getBagLooseCoinsValue(coinCount, existingDenomination);

    existingDenomination.partBagCoins = {
      ...existingDenomination.partBagCoins,
      quantity: quantity ?? undefined,
      value: `${bagValue}`,
    };

    existingDenomination.coin = {
      ...existingDenomination.coin,
      quantity: coinCount ?? undefined,
      value: `${looseCoinsValue}`,
    };
  }
  return existingDenomination;
};
